import React from 'react'
import * as translator from "../../../utils/translator";
import { ContactUsIcon } from "../../../components/ContactUs/ContactUsIcons";
import { getFaqUrl, isWhatsAppAvailable, getIsReturningCustomer, openWhatsAppChat as openWA, openHubSpotChat } from "../../../components/ContactUs/contactUsUtils";
import { useHubSpotChat } from "../../../customHooks/useHubSpotChat";

type Props = {
    language: string,
    country: string;
}

type OptionCardProps = {
    iconKey: string;
    title: string;
    description: React.ReactNode;
    buttonText: string;
    onClick: () => void;
    isBlurred?: boolean;
    unavailableText?: string;
};

const PageOptionCard: React.FC<OptionCardProps> = ({ iconKey, title, description, buttonText, onClick, isBlurred, unavailableText }) => (
    <div className={`contact-us-card--page ${isBlurred ? 'is-blurred' : ''}`}>
        <div className={`flex items-start gap-[14px] flex-1 ${isBlurred ? 'opacity-[0.45]' : ''}`}>
            <div className="contact-us-card__icon">
                <ContactUsIcon iconKey={iconKey} size={28} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2 mb-1">
                    <span className="contact-us-card__title shrink">{title}</span>
                    {isBlurred && unavailableText && (
                        <span className="contact-us-unavailable-badge">{unavailableText}</span>
                    )}
                </div>
                <p className="contact-us-card__description">{description}</p>
            </div>
        </div>
        <div className="mt-3 flex justify-center">
            <button onClick={isBlurred ? undefined : onClick} disabled={isBlurred} className="contact-us-card__btn">{buttonText}</button>
        </div>
    </div>
);

const SupportTicketSection: React.FC<Props> = ({ language, country }) => {
    const isHubSpotAvailable = useHubSpotChat();
    const t = (key: string) => translator.translate(language, "contact", `SupportOptions/${key}`);

    const isGermany = country?.toUpperCase() === 'DE';
    const isRP = getIsReturningCustomer();
    const faqUrl = getFaqUrl(country, language);
    const waAvailable = isWhatsAppAvailable();
    const handleOpenWhatsApp = () => openWA(t('whatsapp_link_number'), t('whatsapp_prefilled_message'));

    const scrollToForm = () => {
        document.querySelector("#contact-order-id-form-container")?.scrollIntoView({ behavior: "smooth" });
    };

    const liveChatCard = (
        <PageOptionCard
            iconKey="chat"
            title={t('live_chat')}
            description={t('live_chat_description')}
            buttonText={t('start_live_chat')}
            onClick={openHubSpotChat}
            isBlurred={!isHubSpotAvailable}
            unavailableText={t('live_chat_unavailable')}
        />
    );

    const whatsAppCard = isGermany ? (
        <PageOptionCard
            iconKey="whatsapp"
            title={t('whatsapp_chat')}
            description={t('whatsapp_description')}
            buttonText={t('open_whatsapp')}
            onClick={handleOpenWhatsApp}
            isBlurred={!waAvailable}
            unavailableText={t('live_chat_unavailable')}
        />
    ) : null;

    const contactFormCard = (
        <PageOptionCard
            iconKey="form"
            title={t('contact_form')}
            description={t('contact_form_description')}
            buttonText={t('submit_ticket')}
            onClick={scrollToForm}
        />
    );

    const helpCenterCard = (
        <PageOptionCard
            iconKey="help"
            title={t('help_center')}
            description={t('help_center_description')}
            buttonText={t('go_to_faq')}
            onClick={() => window.open(faqUrl, '_blank')}
        />
    );

    const rpCards = [liveChatCard, whatsAppCard, contactFormCard, helpCenterCard].filter(Boolean);
    const ftbCards = [contactFormCard, helpCenterCard, liveChatCard].filter(Boolean);
    const cards = isRP ? rpCards : ftbCards;

    return (
        <div className="bg-white rounded-[25px] border border-[#E5E7EB] w-full overflow-hidden">
            <div className="px-6 md:px-10 pt-6 md:pt-8 pb-4">
                <div className="flex items-baseline gap-4 flex-wrap">
                    <h2 className="text-[18px] md:text-[20px] font-bold text-[#1a1a2e] m-0 whitespace-nowrap">{t('title')}</h2>
                    <p className="text-[13px] text-[#6b7280] m-0 leading-[1.4]">
                        {t('subtitle')} <span className="font-semibold text-[#08080A]">{t('hours')}</span>{' '}
                        {language === 'de' && t('suffix')}
                    </p>
                </div>
            </div>

            <hr className="contact-us-section-divider" />

            <div className="px-6 md:px-10 pt-5 pb-6">
                <p className="text-[13px] md:text-[14px] text-[#6b7280] leading-[1.6] m-0 mb-6">{t('description')}</p>

                <div className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${cards.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
                    {cards.map((card, i) => (
                        <div key={i}>{card}</div>
                    ))}
                </div>
            </div>

            <div className="contact-us-footer-container">
                <p className="contact-us-footer-text">
                    <span className="contact-us-footer-label">{t('outside_support_hours')}</span>{' '}
                    {t('outside_support_hours_message')}
                </p>
            </div>
        </div>
    );
}

export default SupportTicketSection
